import React from 'react';
import { FiX, FiFileText, FiPackage, FiDollarSign, FiCreditCard, FiClipboard, FiAlertCircle } from 'react-icons/fi';
import { MdBusiness } from 'react-icons/md';
import StatusBadge from '../StatusBadge/StatusBadge';
import styles from '../../../modules/Orders/Orders.module.css';

const formatCurrency = (value) => {
    if (!value && value !== 0) return 'R$ 0,00';
    return `R$ ${Number(value).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;
};

const formatDate = (dateValue) => {
    if (!dateValue) return '-';
    if (dateValue?.toDate) return dateValue.toDate().toLocaleDateString('pt-BR');
    if (typeof dateValue === 'string') return new Date(dateValue).toLocaleDateString('pt-BR');
    if (dateValue instanceof Date) return dateValue.toLocaleDateString('pt-BR');
    return '-';
};

const ViewOrderModal = ({ order, onClose }) => {
    if (!order) return null;
    const values = order.calculatedValues || {};

    return (
        <div className={styles.modalBackdrop} onClick={onClose}>
            <div className={`${styles.formContainer} ${styles.formContainerLarge}`} onClick={(e) => e.stopPropagation()}>
                <div className={styles.modalHeader}>
                    <div>
                        <h2><FiFileText size={24} style={{ marginRight: '10px' }} /> Visualizar Pedido</h2>
                        <p className={styles.modalSubtitle}>
                            Pedido: <strong>{order.code}</strong>
                        </p>
                    </div>
                    <button type="button" onClick={onClose} className={styles.modalCloseButton}>
                        <FiX size={24} />
                    </button>
                </div>

                <div className={styles.formContent}>
                    {/* Basic Info */}
                    <div className={styles.formSection}>
                        <div className={styles.sectionHeader}>
                            <div className={styles.sectionIcon}><FiFileText size={18} /></div>
                            <h3 className={styles.sectionTitle}>Informações do Pedido</h3>
                        </div>
                        <div className={styles.infoGrid}>
                            <div className={styles.infoItem}>
                                <label>Código</label>
                                <span>{order.code}</span>
                            </div>
                            <div className={styles.infoItem}>
                                <label>Data</label>
                                <span>{formatDate(order.createdAt)}</span>
                            </div>
                            <div className={styles.infoItem}>
                                <label>Status</label>
                                <StatusBadge status={order.status} />
                            </div>
                            <div className={styles.infoItem}>
                                <label>Responsável</label>
                                <span>{order.responsible || order.createdBy || '-'}</span>
                            </div>
                        </div>
                    </div>

                    {/* Client/Supplier Info */}
                    <div className={styles.formSection}>
                        <div className={styles.sectionHeader}>
                            <div className={styles.sectionIcon}><MdBusiness size={18} /></div>
                            <h3 className={styles.sectionTitle}>
                                {order.clientName ? 'Informações do Cliente' : 'Informações do Fornecedor'}
                            </h3>
                        </div>
                        <div className={styles.infoGrid}>
                            <div className={styles.infoItem}>
                                <label>{order.clientName ? 'Cliente' : 'Fornecedor'}</label>
                                <span>{order.clientName || order.supplierName || '-'}</span>
                            </div>
                            {(order.deliveryAddress || order.supplierAddress) && (
                                <div className={styles.infoItem}>
                                    <label>Endereço</label>
                                    <span>{order.deliveryAddress || order.supplierAddress}</span>
                                </div>
                            )}
                            {order.contactName && (
                                <div className={styles.infoItem}>
                                    <label>Contato</label>
                                    <span>{order.contactName} {order.contactPhone && `(${order.contactPhone})`}</span>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Items */}
                    <div className={styles.formSection}>
                        <div className={styles.sectionHeader}>
                            <div className={styles.sectionIcon}><FiPackage size={18} /></div>
                            <h3 className={styles.sectionTitle}>Itens do Pedido</h3>
                            <span className={styles.sectionBadge}>{order.items?.length || 0} itens</span>
                        </div>
                        <div className={styles.tableContainer}>
                            <table className={styles.itemTable}>
                                <thead>
                                    <tr>
                                        <th>#</th>
                                        <th>Código</th>
                                        <th>Nome</th>
                                        <th className="text-right">Qtde</th>
                                        <th className="text-right">Preço Unit.</th>
                                        <th className="text-right">Total</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {order.items?.length > 0 ? (
                                        order.items.map((item, idx) => (
                                            <tr key={idx} className={item.isBonus ? styles.bonusRow : ''}>
                                                <td>{idx + 1}</td>
                                                <td>{item.code || '-'}</td>
                                                <td>
                                                    {item.name || item.commercialName || '-'}
                                                    {item.isBonus && <span className={styles.sampleBadge}>Bônus</span>}
                                                    {item.isSample && <span className={styles.sampleBadge}>Amostra</span>}
                                                </td>
                                                <td className="text-right">{item.quantity || 0}</td>
                                                <td className="text-right">{formatCurrency(item.unitPrice)}</td>
                                                <td className="text-right" style={{ fontWeight: 'bold' }}>{formatCurrency(item.total)}</td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr><td colSpan="6" className={styles.emptyTable}>Nenhum item encontrado</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Values */}
                    <div className={styles.formSection}>
                        <div className={styles.sectionHeader}>
                            <div className={styles.sectionIcon}><FiDollarSign size={18} /></div>
                            <h3 className={styles.sectionTitle}>Resumo de Valores</h3>
                        </div>
                        <div className={styles.resumoGrid}>
                            <div className={styles.resumoLinha}>
                                <span className={styles.resumoLabel}>Subtotal:</span>
                                <span className={styles.resumoValor}>{formatCurrency(values.itemsTotal || values.subtotal)}</span>
                            </div>
                            {values.bonusTotal > 0 && (
                                <div className={styles.resumoLinha}>
                                    <span className={styles.resumoLabel}>Bonificação:</span>
                                    <span className={styles.resumoValor}>{formatCurrency(values.bonusTotal)}</span>
                                </div>
                            )}
                            {values.discountAmount > 0 && (
                                <div className={styles.resumoLinha}>
                                    <span className={styles.resumoLabel}>Desconto:</span>
                                    <span className={styles.resumoValor}>- {formatCurrency(values.discountAmount)}</span>
                                </div>
                            )}
                            {values.ticketDiscountAmount > 0 && (
                                <div className={styles.resumoLinha}>
                                    <span className={styles.resumoLabel}>Desconto Boleto:</span>
                                    <span className={styles.resumoValor}>- {formatCurrency(values.ticketDiscountAmount)}</span>
                                </div>
                            )}
                            <div className={`${styles.resumoLinha} ${styles.resumoTotal}`}>
                                <span className={styles.resumoLabel}>Valor Total:</span>
                                <span className={styles.resumoValorTotal}>{formatCurrency(values.total)}</span>
                            </div>
                        </div>
                    </div>

                    {/* Payment & Delivery */}
                    <div className={styles.formSection}>
                        <div className={styles.sectionHeader}>
                            <div className={styles.sectionIcon}><FiCreditCard size={18} /></div>
                            <h3 className={styles.sectionTitle}>Pagamento e Entrega</h3>
                        </div>
                        <div className={styles.infoGrid}>
                            {order.paymentMethodName && (
                                <div className={styles.infoItem}>
                                    <label>Forma de Pagamento</label>
                                    <span>{order.paymentMethodName}</span>
                                </div>
                            )}
                            {order.billingConditionName && (
                                <div className={styles.infoItem}>
                                    <label>Condição de Faturamento</label>
                                    <span>{order.billingConditionName}</span>
                                </div>
                            )}
                            {order.deliveryDate && (
                                <div className={styles.infoItem}>
                                    <label>Data de Entrega</label>
                                    <span>{formatDate(order.deliveryDate)}</span>
                                </div>
                            )}
                            {order.deliveryType && (
                                <div className={styles.infoItem}>
                                    <label>Tipo de Entrega</label>
                                    <span>{order.deliveryType}</span>
                                </div>
                            )}
                            {order.selectedCarrierName && (
                                <div className={styles.infoItem}>
                                    <label>Transportadora</label>
                                    <span>{order.selectedCarrierName}</span>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Notes */}
                    {(order.commercialNotes || order.productionNotes || order.approvalNotes) && (
                        <div className={styles.formSection}>
                            <div className={styles.sectionHeader}>
                                <div className={styles.sectionIcon}><FiClipboard size={18} /></div>
                                <h3 className={styles.sectionTitle}>Observações</h3>
                            </div>
                            {order.commercialNotes && (
                                <div className={styles.infoBox} style={{ margin: '16px' }}>
                                    <FiAlertCircle size={18} />
                                    <div>
                                        <strong>Observações Comerciais</strong>
                                        <p style={{ margin: '4px 0 0 0' }}>{order.commercialNotes}</p>
                                    </div>
                                </div>
                            )}
                            {order.productionNotes && (
                                <div className={styles.infoBox} style={{ margin: '16px', backgroundColor: 'var(--info-light)', borderLeftColor: 'var(--info-color)' }}>
                                    <FiPackage size={18} />
                                    <div>
                                        <strong>Observações para Produção</strong>
                                        <p style={{ margin: '4px 0 0 0' }}>{order.productionNotes}</p>
                                    </div>
                                </div>
                            )}
                            {order.approvalNotes && (
                                <div className={styles.approvalNotes}>
                                    <strong>Observações da Aprovação</strong>
                                    <p>{order.approvalNotes}</p>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <div className={styles.formActions}>
                    <button type="button" onClick={onClose} className={styles.primaryButton}>
                        <FiX size={18} /> Fechar
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ViewOrderModal;